import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink, FileText, AlertTriangle } from 'lucide-react';
import { jobsApi } from '../../../api/jobs';
import { useLanguage } from '../../../contexts/LanguageContext';
import useEscapeKey from '../../../hooks/useEscapeKey';

interface FinalReportModalProps {
  jobId: string;
  jobTitle: string;
  onClose: () => void;
}

const FinalReportModal = ({ jobId, jobTitle, onClose }: FinalReportModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [reportHtml, setReportHtml] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const reportUrl = jobsApi.getFinalReportUrl(jobId);
  const { t } = useLanguage();
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose);

  // Helper function to clean and prepare HTML for rendering
  const cleanAndPrepareHtml = (html: string): string => {
    if (!html) return '';

    try {
      // Extract only the body content to avoid conflicts with head elements
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find the main content - either in the body or the whole document
      const content = tempDiv.querySelector('body') || tempDiv;
      
      // Fix relative URLs for images, scripts, and links
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:10000';
      
      // Update image sources
      content.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src');
        if (src && !src.startsWith('http') && !src.startsWith('data:')) {
          img.setAttribute('src', `${baseUrl}${src.startsWith('/') ? '' : '/'}${src}`);
        }
      });
      
      // Update links
      content.querySelectorAll('a').forEach(a => {
        const href = a.getAttribute('href');
        if (href && !href.startsWith('http') && !href.startsWith('#') && !href.startsWith('mailto:')) {
          a.setAttribute('href', `${baseUrl}${href.startsWith('/') ? '' : '/'}${href}`);
        }
        
        // Open links in new tab
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });
      
      // Apply hidden class to elements with data-hidden attribute
      content.querySelectorAll('[data-hidden="true"]').forEach(element => {
        element.classList.add('hidden');
      });
      
      // Return just the inner content, not the full HTML document
      return content.innerHTML;
    } catch (error) {
      console.error('Error processing HTML:', error);
      return html; // Return original if processing fails
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const html = await jobsApi.getFinalReport(jobId);
        const cleanedHtml = cleanAndPrepareHtml(html);
        setReportHtml(cleanedHtml);
      } catch (err) {
        console.error('Failed to fetch report:', err);
        setError(t('dashboard.finalReport.error.message'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [jobId]);

  // Open report in new tab
  const openInNewTab = () => {
    window.open(reportUrl, '_blank');
  };

  // Create a blob for download
  const handleDownload = () => {
    if (!reportHtml) return;
    
    // Create a minimal HTML wrapper with only essential styles
    const fullHtml = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t('dashboard.finalReport.title').replace('{jobTitle}', jobTitle)}</title>
        <style>
          /* Minimal print-friendly styles that won't override existing CSS */
          body {
            margin: 0;
            padding: 0;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          .report-container {
            max-width: 1100px;
            margin: 2rem auto;
            padding: 1rem;
          }
          .report-header {
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e7eb;
          }
          .report-footer {
            margin-top: 2rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e7eb;
            font-size: 0.875rem;
            color: #6b7280;
            text-align: center;
          }
          /* Ensure images and tables don't overflow */
          img { max-width: 100%; height: auto; }
          table { max-width: 100%; }
          
          /* Hide elements with hidden class */
          .hidden { display: none !important; }
          
          /* Print optimizations */
          @media print {
            body { 
              background-color: white !important;
              color: black !important;
            }
            .report-container {
              margin: 0;
              padding: 1rem;
              max-width: none;
            }
            a { color: #000 !important; }
            .report-header, .report-footer {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="report-header">
            <h1>${t('dashboard.finalReport.title').replace('{jobTitle}', jobTitle)}</h1>
            <p>${t('dashboard.finalReport.generatedOn').replace('{date}', new Date().toLocaleDateString())}</p>
          </div>
          
          ${reportHtml}
          
          <div class="report-footer">
            <p>${t('dashboard.finalReport.footerMessage')}</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${jobTitle.replace(/\s+/g, '_')}_final_report.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center">
            <FileText className="w-5 h-5 mr-2 text-purple-600" />
            <h2 className="text-xl font-semibold">{t('dashboard.finalReport.title').replace('{jobTitle}', jobTitle)}</h2>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={openInNewTab}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label={t('dashboard.finalReport.openNewTab')}
              title={t('dashboard.finalReport.openNewTab')}
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <button 
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center bg-white p-16">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('dashboard.finalReport.loading')}</p>
            </div>
          </div>
        )}
        
        {/* Error state */}
        {error && (
          <div className="flex flex-col items-center justify-center p-16 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard.finalReport.error.title')}</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.open(reportUrl, '_blank')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('dashboard.finalReport.error.tryNewTab')}
            </button>
          </div>
        )}
        
        {/* Report content */}
        {!isLoading && !error && (
          <div className="flex-1 overflow-auto">
            {/* Use an iframe to provide style isolation */}
            <iframe
              title={`Final Report: ${jobTitle}`}
              className="w-full h-full border-0"
              srcDoc={`
                <!DOCTYPE html>
                <html lang="en">
                <head>
                  <meta charset="UTF-8">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title>${t('dashboard.finalReport.title').replace('{jobTitle}', jobTitle)}</title>
                  <style>
                    /* Minimal styling to ensure proper display without overriding existing styles */
                    html, body {
                      margin: 0;
                      padding: 0;
                      height: 100%;
                      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                      overflow: auto;
                    }
                    
                    /* Only add styles for iframe container, not content */
                    .report-wrapper {
                      max-width: 1200px;
                      margin: 0 auto;
                      padding: 1.5rem;
                      transform-origin: top center;
                      transform: scale(0.95);
                      min-height: 100%;
                      overflow-y: auto;
                    }
                    
                    /* Hide elements with the hidden class */
                    .hidden { display: none !important; }
                    
                    /* Simple styles to ensure images and tables behave */
                    img { max-width: 100%; height: auto; }
                    table { max-width: 100%; }
                    
                    /* Make links open in new tab */
                    a {
                      target: _blank;
                      rel: noopener noreferrer;
                    }
                    
                    /* Add some spacing for better readability */
                    .report-wrapper > * {
                      margin-bottom: 1rem;
                    }
                  </style>
                </head>
                <body>
                  <div class="report-wrapper">
                    ${reportHtml}
                  </div>
                </body>
                </html>
              `}
              sandbox="allow-same-origin allow-scripts"
              onLoad={() => setIsLoading(false)}
            />
          </div>
        )}
        
        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {t('dashboard.finalReport.viewingMessage').replace('{jobTitle}', jobTitle)}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('common.close')}
            </button>
            <button
              onClick={handleDownload}
              disabled={isLoading || !!error}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:bg-purple-300 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4 mr-1" />
              {t('common.download')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinalReportModal;